import React from "react";
import { CustomButton } from "./CustomButton";

interface AddToGoogleCalendarButtonProps {
  title: string;
  description?: string;
  location?: string;
  startDateTime?: string;
  endDateTime?: string;
  buttonText?: string;
  className?: string;
  variant?: "primary" | "secondary" | "outline" | "danger" | "gray";
  isLoading?: boolean;
}

/** In UTC with its Z: without a zone, Google reads the time in the zone of the Google account, not of the booking. */
export const formatDateForGoogle = (dateString: string): string =>
  new Date(dateString)
    .toISOString()
    .replace(/\.\d{3}Z$/, "Z")
    .replace(/[-:]/g, "");

const AddToGoogleCalendarButton: React.FC<AddToGoogleCalendarButtonProps> = ({
  title,
  description = "",
  location = "",
  startDateTime,
  endDateTime,
  buttonText = "Add to Google Calendar",
  className = "",
  variant = "primary",
  isLoading = false,
}) => {
  const generateGoogleCalendarUrl = (): string => {
    const baseUrl = "https://calendar.google.com/calendar/render";
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: title,
    });

    if (description) {
      params.append("details", description);
    }

    if (location) {
      params.append("location", location);
    }

    const start = startDateTime || new Date().toISOString();
    const end =
      endDateTime || new Date(Date.now() + 60 * 60 * 1000).toISOString();

    const formattedStart = formatDateForGoogle(start);
    const formattedEnd = formatDateForGoogle(end);
    params.append("dates", `${formattedStart}/${formattedEnd}`);

    return `${baseUrl}?${params.toString()}`;
  };

  const handleClick = () => {
    const url = generateGoogleCalendarUrl();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <CustomButton
      type="button"
      onClick={handleClick}
      variant={variant}
      icon="calendarCurrent"
      title={buttonText}
      className={className}
      isLoading={isLoading}
      aria-label={buttonText}
    />
  );
};

export default AddToGoogleCalendarButton;
